import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    Link,
    Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { selectedUsersAtom, usersMapAtom, datesAtom } from '../jotai/atoms';
import { post } from '../requests';
import { useMessage } from '../hooks';
import moment from 'moment';
import CreateWorkLogFromRecommendationDialog from './CreateWorkLogFromRecommendationDialog';
import AddIcon from '@mui/icons-material/Add';

export function RecommendationsTable() {
    const { t } = useTranslation();
    const { showError } = useMessage();
    const selectedUsers = useAtomValue(selectedUsersAtom);
    const usersMap = useAtomValue(usersMapAtom);
    const dates = useAtomValue(datesAtom);
    
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef(null);
    const timeoutRef = useRef(null);
    const lastRequestKeyRef = useRef(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);

    // Memoize request key to avoid unnecessary requests
    const requestKey = useMemo(() => {
        if (selectedUsers.length !== 1 || dates.length === 0) {
            return null;
        }

        const userId = selectedUsers[0];
        const user = usersMap[userId];
        
        if (!user || !user.email) {
            return null;
        }

        // Extract date from first and last date objects
        const firstDateObj = dates[0];
        const lastDateObj = dates[dates.length - 1];
        
        let dateFrom = firstDateObj.date;
        let dateTo = lastDateObj.date;
        
        if (firstDateObj.grouped && firstDateObj.includes && firstDateObj.includes.length > 0) {
            dateFrom = firstDateObj.includes[0];
        } else if (!dateFrom) {
            dateFrom = firstDateObj;
        }
        
        if (lastDateObj.grouped && lastDateObj.includes && lastDateObj.includes.length > 0) {
            dateTo = lastDateObj.includes[lastDateObj.includes.length - 1];
        } else if (!dateTo) {
            dateTo = lastDateObj;
        }
        
        const dateFromMoment = moment.isMoment(dateFrom) ? dateFrom : moment(dateFrom);
        const dateToMoment = moment.isMoment(dateTo) ? dateTo : moment(dateTo);

        return `${user.email}_${dateFromMoment.format('YYYY-MM-DD')}_${dateToMoment.format('YYYY-MM-DD')}`;
    }, [selectedUsers, dates, usersMap]);

    useEffect(() => {
        // Cancel previous request if exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Only fetch recommendations if exactly one user is selected
        if (!requestKey) {
            // Don't clear issues immediately, only clear if user/dates actually changed
            if (selectedUsers.length !== 1 || dates.length === 0) {
                setIssues([]);
            }
            setLoading(false);
            return;
        }

        // Check if request key changed - if not, don't make new request
        if (lastRequestKeyRef.current === requestKey) {
            return;
        }

        // Debounce requests to avoid rate limiting
        timeoutRef.current = setTimeout(() => {
            // Capture requestKey at the time of execution
            const currentRequestKey = requestKey;
            
            // If request key changed during debounce, don't proceed
            if (lastRequestKeyRef.current === currentRequestKey) {
                return;
            }

            const fetchRecommendations = async () => {
                // Create new AbortController for this request
                abortControllerRef.current = new AbortController();
                
                setLoading(true);
                try {
                    const userId = selectedUsers[0];
                    const user = usersMap[userId];
                    
                    if (!user || !user.email) {
                        setIssues([]);
                        setLoading(false);
                        lastRequestKeyRef.current = currentRequestKey;
                        return;
                    }

                    // Get date range from dates array
                    const firstDateObj = dates[0];
                    const lastDateObj = dates[dates.length - 1];
                    
                    let dateFrom = firstDateObj.date;
                    let dateTo = lastDateObj.date;
                    
                    if (firstDateObj.grouped && firstDateObj.includes && firstDateObj.includes.length > 0) {
                        dateFrom = firstDateObj.includes[0];
                    } else if (!dateFrom) {
                        dateFrom = firstDateObj;
                    }
                    
                    if (lastDateObj.grouped && lastDateObj.includes && lastDateObj.includes.length > 0) {
                        dateTo = lastDateObj.includes[lastDateObj.includes.length - 1];
                    } else if (!dateTo) {
                        dateTo = lastDateObj;
                    }
                    
                    const dateFromMoment = moment.isMoment(dateFrom) ? dateFrom : moment(dateFrom);
                    const dateToMoment = moment.isMoment(dateTo) ? dateTo : moment(dateTo);

                    const response = await post('/api/v1/user_issues', {
                        user_identity: user.email,
                        date_from: dateFromMoment.format('YYYY-MM-DD'),
                        date_to: dateToMoment.format('YYYY-MM-DD'),
                        limit: 15
                    });

                    // Check if request was aborted or key changed
                    if (abortControllerRef.current?.signal.aborted) {
                        return;
                    }

                    // Update key only after successful response
                    lastRequestKeyRef.current = currentRequestKey;

                    if (response && response.issues) {
                        setIssues(response.issues.slice(0, 15)); // Ensure max 15
                    } else {
                        // Only clear if we got a response but no issues
                        if (response) {
                            setIssues([]);
                        }
                    }
                } catch (err) {
                    // Ignore abort errors
                    if (err.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
                        return;
                    }
                    
                    console.error('Failed to fetch recommendations:', err);
                    
                    // Handle rate limit errors gracefully - don't clear existing issues
                    if (err.code === 429 || (err.message && err.message.includes('rate limit'))) {
                        // Keep existing issues, just stop loading
                        // Don't update lastRequestKeyRef so we can retry later
                    } else {
                        // Show error for other failures, but keep existing issues if any
                        const errorMessage = err.message || t('components:recommendations_table.error', 'Не удалось загрузить рекомендации');
                        showError(errorMessage);
                        // Only clear if we don't have issues already
                        if (issues.length === 0) {
                            setIssues([]);
                        }
                        // Update key even on error to prevent infinite retries
                        lastRequestKeyRef.current = currentRequestKey;
                    }
                } finally {
                    if (!abortControllerRef.current?.signal.aborted) {
                        setLoading(false);
                    }
                }
            };

            fetchRecommendations();
        }, 800); // Increased debounce to 800ms

        // Cleanup function
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [requestKey, selectedUsers, dates, usersMap, showError, t, issues.length]);

    // Don't render if not exactly one user is selected
    if (selectedUsers.length !== 1) {
        return null;
    }

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('components:recommendations_table.title', 'Рекомендации')}
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                {t('components:recommendations_table.column.key', 'Ключ')}
                            </TableCell>
                            <TableCell>
                                {t('components:recommendations_table.column.summary', 'Название')}
                            </TableCell>
                            <TableCell>
                                {t('components:recommendations_table.column.project', 'Проект')}
                            </TableCell>
                            <TableCell>
                                {t('components:recommendations_table.column.action', 'Действие')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {t('common:loading', 'Загрузка...')}
                                </TableCell>
                            </TableRow>
                        ) : issues.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {t('components:recommendations_table.no_issues', 'Задачи не найдены')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            issues.map((issue) => {
                                // Get date from dates array for this issue
                                let issueDate = moment();
                                if (dates.length > 0) {
                                    const firstDateObj = dates[0];
                                    let dateFrom = firstDateObj.date;
                                    if (firstDateObj.grouped && firstDateObj.includes && firstDateObj.includes.length > 0) {
                                        dateFrom = firstDateObj.includes[0];
                                    } else if (!dateFrom) {
                                        dateFrom = firstDateObj;
                                    }
                                    issueDate = moment.isMoment(dateFrom) ? dateFrom : moment(dateFrom);
                                }

                                return (
                                    <TableRow key={issue.key}>
                                        <TableCell>
                                            <Link
                                                href={`https://tracker.yandex.ru/${issue.key}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {issue.key}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{issue.summary}</TableCell>
                                        <TableCell>
                                            {issue.project ? issue.project.name : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                    setSelectedIssue(issue);
                                                    setSelectedDate(issueDate);
                                                    setDialogOpen(true);
                                                }}
                                            >
                                                {t('components:recommendations_table.add_to_timesheet', 'Добавить в timesheet')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <CreateWorkLogFromRecommendationDialog
                isOpen={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setSelectedIssue(null);
                    setSelectedDate(null);
                }}
                issue={selectedIssue}
                initialDate={selectedDate}
            />
        </Box>
    );
}

